import * as Sentry from '@sentry/react';
import { initSentry } from '@odo/shared-ui/observability/sentry';
import { sanitizeUrl } from '@odo/shared-ui/observability/sanitizeUrl';

/**
 * Sentry renders promise rejections whose reason is a DOM Event or plain
 * object as an opaque "<unknown>" issue with no stack (see the long-running
 * "Event `Event` (type=error) captured as promise rejection" issue). This
 * probe runs BEFORE Sentry's own unhandledrejection handler (listener
 * registration order) and stashes a serializable description of the reason;
 * beforeSend attaches it as context so the next occurrence identifies itself.
 */
let lastOpaqueRejection: Record<string, unknown> | null = null;

function describeEventTarget(target: EventTarget | null): Record<string, unknown> {
    if (target === null) return { target: null };
    const description: Record<string, unknown> = {
        targetConstructor: target.constructor?.name ?? 'unknown'
    };
    if (target instanceof HTMLElement) {
        description['tagName'] = target.tagName;
        const el = target as HTMLElement & { src?: string; href?: string; currentSrc?: string };
        if (el.src) description['src'] = sanitizeUrl(el.src);
        if (el.href) description['href'] = sanitizeUrl(el.href);
        if (el.currentSrc) description['currentSrc'] = sanitizeUrl(el.currentSrc);
    }
    return description;
}

function describeOpaqueReason(reason: unknown): Record<string, unknown> | null {
    if (reason instanceof Error) return null; // Sentry handles these fine
    if (reason instanceof Event) {
        return {
            kind: 'dom-event',
            eventType: reason.type,
            isTrusted: reason.isTrusted,
            timeStamp: reason.timeStamp,
            ...describeEventTarget(reason.target)
        };
    }
    if (typeof reason === 'object' && reason !== null) {
        // Keys only, never values - the object may contain PHI.
        return {
            kind: 'plain-object',
            reasonConstructor: reason.constructor?.name ?? 'unknown',
            keys: Object.keys(reason).slice(0, 20)
        };
    }
    return null;
}

let probeRegistered = false;

function registerOpaqueRejectionProbe(): void {
    if (probeRegistered) return;
    probeRegistered = true;
    // Single last-wins slot: two opaque rejections in the same processing
    // window can mis-attribute the first one's description. Accepted for a
    // diagnostic probe - the attach gate below keeps it off unrelated events,
    // and beforeSend clears the slot every rejection event (attached or not).
    window.addEventListener('unhandledrejection', (event) => {
        lastOpaqueRejection = describeOpaqueReason(event.reason);
    });
}

// The SDK namespaces this as 'auto.browser.global_handlers.onunhandledrejection'
// (see @sentry/browser globalhandlers.js) rather than the bare string - match
// either form.
function isUnhandledRejectionMechanism(mechanismType: string | undefined): boolean {
    return typeof mechanismType === 'string' && mechanismType.endsWith('onunhandledrejection');
}

/** True only for the SDK's synthetic non-Error rejection events - the ones the
 *  probe describes. Error-reason rejections have a real type/stack and must
 *  never inherit a stale opaque description. */
function isOpaqueRejectionEvent(event: Sentry.ErrorEvent): boolean {
    return (event.exception?.values ?? []).some(
        (ex) =>
            isUnhandledRejectionMechanism(ex.mechanism?.type) &&
            (ex.type === 'Event' ||
                ex.type === 'UnhandledRejection' ||
                (ex.value ?? '').includes('captured as promise rejection'))
    );
}

function attachOpaqueRejectionContext(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
    const isRejectionEvent = (event.exception?.values ?? []).some((ex) =>
        isUnhandledRejectionMechanism(ex.mechanism?.type)
    );
    if (!isRejectionEvent) return event;
    const stashed = lastOpaqueRejection;
    // Every rejection event consumes the slot - an Error-reason rejection
    // clears a stale opaque description instead of letting it leak forward.
    lastOpaqueRejection = null;
    if (stashed === null || !isOpaqueRejectionEvent(event)) return event;
    return {
        ...event,
        contexts: { ...event.contexts, opaque_rejection: stashed }
    };
}

function initDashboardSentry(): void {
    const dsn = import.meta.env.VITE_DASHBOARD_SENTRY_DSN;
    if (!dsn) return;

    // Must precede Sentry.init: same-event listeners fire in registration
    // order, and the probe must observe the rejection before Sentry captures.
    registerOpaqueRejectionProbe();

    initSentry({ dsn, release: __APP_VERSION__, enrichEvent: attachOpaqueRejectionContext });
}

interface UploadErrorContext {
    /** Display name of the file that failed */
    fileName: string;
    /** File size in bytes */
    fileSize: number;
    /** MIME type reported by the browser */
    mimeType?: string;
    /** Which part of the app triggered the upload (e.g. 'files_tab', 'chat') */
    uploadContext?: string;
}

/**
 * Captures a file upload failure to Sentry with structured context.
 * Call this alongside the existing reportError() call so upload failures are
 * visible in both our backend error log and Sentry.
 */
function captureUploadError(error: unknown, context: UploadErrorContext): void {
    Sentry.withScope((scope) => {
        scope.setTag('error.type', 'upload_failure');
        scope.setTag('upload.context', context.uploadContext ?? 'unknown');

        scope.setContext('upload', {
            fileName: context.fileName,
            fileSizeBytes: context.fileSize,
            mimeType: context.mimeType ?? 'unknown',
            uploadContext: context.uploadContext ?? 'unknown'
        });

        scope.setFingerprint(['upload-error', context.uploadContext ?? 'unknown']);

        Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
    });
}

/**
 * Captures an appointments-PDF generation failure. Tagged separately so a real
 * render bug stays distinguishable from a post-deploy lazy-chunk load failure.
 */
function captureAppointmentsPdfError(error: unknown): void {
    Sentry.withScope((scope) => {
        scope.setTag('error.type', 'appointments_pdf_failure');
        scope.setFingerprint(['appointments-pdf-error']);
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
    });
}

export { initDashboardSentry, captureUploadError, captureAppointmentsPdfError };
// Exposed for verification harnesses only - not part of the app-facing API.
export { describeOpaqueReason, registerOpaqueRejectionProbe, attachOpaqueRejectionContext };
